import React, { useEffect, useState } from 'react';
import axios from 'axios';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Button } from '../components/ui/button';
import { Heart, Star, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Shows unread motivation/thanks messages as a beautiful modal sequence.
 * Built on Radix Dialog primitives directly (no built-in X close button).
 * Mounted at app root; polls on login + every 60s.
 */
export default function EmployeeMessageModal() {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]); // unread messages
  const [current, setCurrent] = useState(null);

  const fetchUnread = async () => {
    if (!user || !user.employee_id) return;
    try {
      const r = await axios.get(`${API_URL}/api/employee-messages/me`, { params: { unread_only: true } });
      const items = r.data?.items || [];
      if (items.length > 0) {
        setQueue(items);
        setCurrent(items[0]);
      }
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    if (!user) return;
    fetchUnread();
    const id = setInterval(fetchUnread, 60000);
    return () => clearInterval(id);
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismiss = async () => {
    if (!current) return;
    try { await axios.post(`${API_URL}/api/employee-messages/${current.id}/read`); } catch (e) { /* ignore */ }
    const remaining = queue.filter((m) => m.id !== current.id);
    setQueue(remaining);
    setCurrent(remaining.length > 0 ? remaining[0] : null);
  };

  if (!current) return null;

  const isMotivation = current.message_type === 'motivation';
  const Icon = isMotivation ? Star : Heart;
  const titleText = isMotivation ? 'رسالة تحفيزية لك' : 'رسالة شكر لك';
  const accentBg = isMotivation
    ? 'from-sky-500 via-blue-600 to-indigo-700'
    : 'from-teal-500 via-cyan-600 to-blue-700';

  return (
    <DialogPrimitive.Root open={!!current}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          dir="rtl"
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          className="fixed left-[50%] top-[50%] z-50 grid w-[calc(100%-2rem)] max-w-sm mx-4 translate-x-[-50%] translate-y-[-50%] gap-0 bg-background shadow-2xl border-0 rounded-2xl overflow-hidden duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          data-testid="employee-message-modal"
        >
          <DialogPrimitive.Title className="sr-only">{titleText}</DialogPrimitive.Title>

          {/* Confetti-like top banner */}
          <div className={`relative bg-gradient-to-br ${accentBg} p-5 text-white text-center`}>
            <div className="absolute inset-0 opacity-20 pointer-events-none">
              <div className="absolute top-2 left-4 text-xl animate-bounce" style={{ animationDelay: '0ms' }}>✨</div>
              <div className="absolute top-6 right-6 text-2xl animate-bounce" style={{ animationDelay: '200ms' }}>🌟</div>
              <div className="absolute bottom-4 left-8 text-lg animate-bounce" style={{ animationDelay: '400ms' }}>⭐</div>
              <div className="absolute bottom-2 right-4 text-xl animate-bounce" style={{ animationDelay: '600ms' }}>🎉</div>
            </div>
            <div className="relative">
              <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-white/20 ring-4 ring-white/30 mb-2">
                <Icon className="h-7 w-7" />
              </div>
              <h2 className="text-lg font-black">{titleText}</h2>
              <p className="text-xs opacity-90 mt-1">من: {current.sent_by_name || 'الإدارة العامة'}</p>
            </div>
          </div>

          {/* Message body */}
          <div className="p-5 space-y-3 bg-background">
            <p className="text-sm leading-relaxed text-foreground text-center" data-testid="employee-message-text">
              {current.message}
            </p>
            <div className="text-[10px] text-foreground/50 text-center font-numbers">
              {new Date(current.sent_at).toLocaleString('ar-EG', { dateStyle: 'full', timeStyle: 'short' })}
            </div>
            <Button onClick={dismiss} className="w-full h-10 gap-2 rounded-xl" data-testid="dismiss-message-btn">
              <CheckCircle2 className="h-4 w-4" />
              {queue.length > 1 ? `استلمت الرسالة (${queue.length - 1} أخرى)` : 'استلمت الرسالة'}
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
